import * as React from 'react'

import { SplitsLink } from '~/components/Link'
import { cn } from '~/utils/tailwind-utils'

const TABLE_ROW_HOVER_BG = 'hover:bg-gray-50 dark:hover:bg-gray-800'

const Table = ({
  ref,
  className,
  wrapperClassName,
  allowOverflow = false,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  className?: string
  wrapperClassName?: string
  allowOverflow?: boolean
} & { ref?: React.RefObject<HTMLTableElement | null> }) => (
  <div
    className={cn(
      'relative w-full outline-none',
      {
        'overflow-auto': !allowOverflow,
      },
      wrapperClassName,
    )}
  >
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
)
Table.displayName = 'Table'

const TableHeader = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  className?: string
} & { ref?: React.RefObject<HTMLTableSectionElement | null> }) => (
  <thead
    ref={ref}
    className={cn(
      'sticky top-[var(--tabs-list-height,0px)] z-10 bg-white dark:bg-black',
      className,
    )}
    {...props}
  />
)
TableHeader.displayName = 'TableHeader'

const TableBody = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement> & {
  className?: string
} & { ref?: React.RefObject<HTMLTableSectionElement | null> }) => (
  <tbody
    ref={ref}
    className={cn(
      '[&_tr:last-child]:border-0 [&_tr:last-child_td]:border-b-0',
      className,
    )}
    {...props}
  />
)
TableBody.displayName = 'TableBody'

const TableRow = ({
  ref,
  className,
  secondaryDisplay,
  highlightOnHover = true,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & {
  className?: string
  secondaryDisplay?: React.JSX.Element
  highlightOnHover?: boolean
} & { ref?: React.RefObject<HTMLTableRowElement | null> }) => (
  <tr
    ref={ref}
    className={cn(
      'group relative border-b-0 transition-colors data-[state=selected]:bg-gray-100 dark:data-[state=selected]:bg-gray-800',
      {
        [TABLE_ROW_HOVER_BG]: highlightOnHover,
      },
      className,
    )}
    {...props}
  >
    {props.children}
    {secondaryDisplay && (
      <td className="w-0 p-0">
        <div className="absolute inset-y-0 right-3 hidden group-hover:flex group-has-[[data-state=open]]:flex">
          {secondaryDisplay}
        </div>
      </td>
    )}
  </tr>
)
TableRow.displayName = 'TableRow'

type CellSize = 'base' | 'small'

const TableHead = ({
  ref,
  className,
  size = 'base',
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & {
  className?: string
  size?: CellSize
} & { ref?: React.RefObject<HTMLTableCellElement | null> }) => (
  <th
    ref={ref}
    className={cn(
      'relative z-[11] bg-white px-3 text-left align-middle font-medium text-secondary shadow-[inset_0_-1px_0_0_rgb(229,231,235)] dark:bg-black dark:shadow-[inset_0_-1px_0_0_rgb(38,38,38)] [&:has([role=checkbox])]:pr-0',
      size === 'small' ? 'py-2' : 'py-3',
      'first:pl-4 last:pr-4 md:first:pl-6 md:last:pr-6 lg:first:pl-8 lg:last:pr-8',
      className,
    )}
    {...props}
  />
)
TableHead.displayName = 'TableHead'

const TableCell = ({
  ref,
  className,
  children,
  path,
  size = 'base',
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  className?: string
  path?: string
  size?: CellSize
} & { ref?: React.RefObject<HTMLTableCellElement | null> }) => {
  const isLink = !!path
  const verticalPadding = size === 'small' ? 'py-2' : 'py-3'
  const firstLastPadding =
    'first:pl-4 last:pr-4 md:first:pl-6 md:last:pr-6 lg:first:pl-8 lg:last:pr-8'

  return (
    <td
      ref={ref}
      className={cn(
        'overflow-hidden border-b border-gray-100 align-middle dark:border-gray-800',
        firstLastPadding,
        // When cell has a link, horizontal padding is on td, vertical on link
        {
          'relative isolate px-3': isLink,
          [cn('px-3 [&:has([role=checkbox])]:pr-0', verticalPadding)]: !isLink,
        },
        className,
      )}
      {...props}
    >
      {path ? (
        <SplitsLink
          path={path}
          // This after:* classes makes the link to expand through the whole cell height or width
          // without impacting the table layout
          className={cn(
            'block w-full select-text truncate after:absolute after:left-0 after:top-0 after:h-full after:w-full',
            // Makes children to be on top of the cell link so any click listener/link works normally
            '[&>*]:relative [&>*]:z-10',
            verticalPadding, // Only vertical padding - horizontal handled by td
          )}
          draggable="false"
        >
          {children}
        </SplitsLink>
      ) : (
        children
      )}
    </td>
  )
}
TableCell.displayName = 'TableCell'

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell }
